import { supabase } from '../lib/supabase';
import type {
  Lead,
  LeadTag,
  LeadActivity,
  LeadWithTags,
  CreateLeadInput,
  UpdateLeadInput,
  CreateActivityInput,
  LeadStatus,
  LeadType,
} from '../types/crm';
import { createTodoForNewLead } from './todoService';
import { logger } from '../lib/logger';

export interface LeadsFilter {
  search?: string;
  status?: LeadStatus | 'ALL';
  leadType?: LeadType | 'ALL';
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface LeadsResponse {
  leads: LeadWithTags[];
  totalCount: number;
}

export async function getLeads(
  userId: string,
  filters: LeadsFilter = {},
  pagination: PaginationParams = { page: 1, pageSize: 20 }
): Promise<LeadsResponse> {
  const { page, pageSize } = pagination;
  const offset = (page - 1) * pageSize;

  let query = supabase
    .from('leads')
    .select('*', { count: 'exact' })
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (filters.search) {
    query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
  }

  if (filters.status && filters.status !== 'ALL') {
    query = query.eq('status', filters.status);
  }

  if (filters.leadType && filters.leadType !== 'ALL') {
    query = query.eq('lead_type', filters.leadType);
  }

  query = query.range(offset, offset + pageSize - 1);

  const { data: leads, error, count } = await query;

  if (error) throw error;
  if (!leads) return { leads: [], totalCount: 0 };

  const leadIds = leads.map((l) => l.id);
  const { data: assignments } = await supabase
    .from('lead_tag_assignments')
    .select('lead_id, tag_id, lead_tags(*)')
    .in('lead_id', leadIds);

  const tagsByLeadId: Record<string, LeadTag[]> = {};
  assignments?.forEach((a) => {
    if (!tagsByLeadId[a.lead_id]) {
      tagsByLeadId[a.lead_id] = [];
    }
    if (a.lead_tags) {
      tagsByLeadId[a.lead_id].push(a.lead_tags as unknown as LeadTag);
    }
  });

  const leadsWithTags: LeadWithTags[] = leads.map((lead) => ({
    ...lead,
    tags: tagsByLeadId[lead.id] || [],
  }));

  return { leads: leadsWithTags, totalCount: count || 0 };
}

export async function getLeadById(leadId: string, userId: string): Promise<LeadWithTags | null> {
  const { data: lead, error } = await supabase
    .from('leads')
    .select('*')
    .eq('id', leadId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  if (!lead) return null;

  const { data: assignments } = await supabase
    .from('lead_tag_assignments')
    .select('tag_id, lead_tags(*)')
    .eq('lead_id', leadId);

  const tags: LeadTag[] = assignments
    ?.map((a) => a.lead_tags as unknown as LeadTag)
    .filter(Boolean) || [];

  return { ...lead, tags };
}

export async function createLead(userId: string, input: CreateLeadInput): Promise<Lead> {
  const { tag_ids, ...leadData } = input;

  const { data: lead, error } = await supabase
    .from('leads')
    .insert({
      ...leadData,
      user_id: userId,
      last_activity_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;

  if (tag_ids && tag_ids.length > 0) {
    const assignments = tag_ids.map((tag_id) => ({
      lead_id: lead.id,
      tag_id,
    }));
    await supabase.from('lead_tag_assignments').insert(assignments);
  }

  await supabase.from('lead_activities').insert({
    user_id: userId,
    lead_id: lead.id,
    type: 'CREATED',
    content: 'Lead erstellt',
  });

  // Create todo for new leads with status 'NEW'
  const leadStatus = lead.status || 'NEW';
  if (leadStatus === 'NEW') {
    try {
      await createTodoForNewLead({
        userId,
        leadId: lead.id,
        leadName: lead.full_name,
        source: 'manual',
      });
    } catch (todoError) {
      logger.error('Error creating todo for new lead:', todoError);
    }
  }

  return lead;
}

export async function updateLead(
  leadId: string,
  userId: string,
  input: UpdateLeadInput
): Promise<Lead> {
  const { tag_ids, ...leadData } = input;

  const updateData: Record<string, unknown> = { ...leadData };

  const { data: lead, error } = await supabase
    .from('leads')
    .update(updateData)
    .eq('id', leadId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;

  if (tag_ids !== undefined) {
    await supabase
      .from('lead_tag_assignments')
      .delete()
      .eq('lead_id', leadId);

    if (tag_ids.length > 0) {
      const assignments = tag_ids.map((tag_id) => ({
        lead_id: leadId,
        tag_id,
      }));
      await supabase.from('lead_tag_assignments').insert(assignments);
    }
  }

  return lead;
}

export async function updateLeadStatus(
  leadId: string,
  userId: string,
  oldStatus: LeadStatus,
  newStatus: LeadStatus
): Promise<Lead> {
  const { data: lead, error } = await supabase
    .from('leads')
    .update({
      status: newStatus,
      last_activity_at: new Date().toISOString(),
    })
    .eq('id', leadId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;

  await supabase.from('lead_activities').insert({
    user_id: userId,
    lead_id: leadId,
    type: 'STATUS_CHANGE',
    content: `Status geändert von ${oldStatus} zu ${newStatus}`,
  });

  return lead;
}

export async function deleteLead(leadId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .delete()
    .eq('id', leadId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function getTags(userId: string): Promise<LeadTag[]> {
  const { data, error } = await supabase
    .from('lead_tags')
    .select('*')
    .eq('user_id', userId)
    .order('name');

  if (error) throw error;
  return data || [];
}

export async function createTag(userId: string, name: string): Promise<LeadTag> {
  const { data, error } = await supabase
    .from('lead_tags')
    .insert({ user_id: userId, name })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getActivities(leadId: string, userId: string): Promise<LeadActivity[]> {
  const { data, error } = await supabase
    .from('lead_activities')
    .select('*')
    .eq('lead_id', leadId)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createActivity(
  userId: string,
  input: CreateActivityInput
): Promise<LeadActivity> {
  const { data: activity, error } = await supabase
    .from('lead_activities')
    .insert({
      ...input,
      user_id: userId,
    })
    .select()
    .single();

  if (error) throw error;

  await supabase
    .from('leads')
    .update({ last_activity_at: new Date().toISOString() })
    .eq('id', input.lead_id)
    .eq('user_id', userId);

  return activity;
}
